import { useEffect, useState } from "react";
import { ReactComponent as TimesIcon } from "../icons/times.svg";
import { ReactComponent as SquareIcon } from "../icons/square.svg";
import { ReactComponent as CheckSquareIcon } from "../icons/check-square.svg";
import StatusIcon from "../ui/StatusIcon";
import { usePractice } from "../usePractice";
import { getBrand } from "../utils/brand";
import { openBodyBagTagPDF } from "../services/bodyBagTag";

export function CaseDetails({ caseData, setCaseToView }) {
  const { practice } = usePractice();
  const brand = getBrand(practice?.fields?.brand);
  const [isLoadingTag, setIsLoadingTag] = useState(false);

  // Listen for Escape key to close modal
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        setCaseToView(null);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setCaseToView]);

  async function viewBodyBagTag() {
    setIsLoadingTag(true);
    try {
      const fields = caseData.fields;
      await openBodyBagTagPDF({
        tagNumber: fields.tagNumber,
        petName: fields.petName,
        species: fields.species,
        otherSpecies: fields.otherSpecies,
        furColor: fields.furColor,
        weight: fields.weight,
        aquamationType: fields.aquamationType,
        ownerName: fields.ownerName,
        submitterInfo: fields.submitterInfo,
        practiceName: practice?.fields?.practiceName || fields.practiceName,
        brand,
      });
    } catch {
      // Error already surfaced in openBodyBagTagPDF
    } finally {
      setIsLoadingTag(false);
    }
  }

  if (!caseData) return null;

  return (
    <div className="modal case-details">
      <div className="modal-content">
        <div className="close" onClick={() => setCaseToView(null)}>
          <TimesIcon className="fill-black-900" />
        </div>

        <div className="modal-inner">
          <h2>Case #{caseData.fields.tagNumber}</h2>

          <p className="gap-1 mt-2">
            {caseData.fields.casePDF?.length > 0 && (
              <a
                className="button"
                href={caseData.fields.casePDF[0].url}
                target="_blank"
                rel="noreferrer"
              >
                Download Case Details
              </a>
            )}
            {caseData.fields.tagNumber != null && (
              <button
                type="button"
                className="button"
                onClick={viewBodyBagTag}
                disabled={isLoadingTag}
                data-cy="view-body-bag-tag-button"
              >
                {isLoadingTag ? "Preparing..." : "Download Body Bag Tag"}
              </button>
            )}
          </p>

          <div className="flex flex-wrap gap-3">
            <div>
              <h4 className="mt-2">Pet Details</h4>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <strong>Name</strong>
                    </td>
                    <td>{caseData.fields.petName}</td>
                  </tr>
                  {caseData.fields.preferredPetName && (
                    <tr>
                      <td>
                        <strong>Preferred Name</strong>
                      </td>
                      <td>{caseData.fields.preferredPetName}</td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      <strong>Weight</strong>
                    </td>
                    <td>{caseData.fields.weight}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Sex</strong>
                    </td>
                    <td>{caseData.fields.sex}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Species</strong>
                    </td>
                    <td>
                      {caseData.fields.species}
                      {caseData.fields.otherSpecies
                        ? ` - ${caseData.fields.otherSpecies}`
                        : ""}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Breed</strong>
                    </td>
                    <td>{caseData.fields.breed}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Fur/Body Color</strong>
                    </td>
                    <td>{caseData.fields.furColor}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Unique Identifiers</strong>
                    </td>
                    <td>{caseData.fields.uniqueIdentifiers}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <h4 className="mt-2">Owner Details</h4>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <strong>Name</strong>
                    </td>
                    <td>{caseData.fields.ownerName}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Preferred Name</strong>
                    </td>
                    <td>{caseData.fields.preferredPetName}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Phone</strong>
                    </td>
                    <td>{caseData.fields.ownerPhone}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Email</strong>
                    </td>
                    <td>{caseData.fields.ownerEmail}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Second Owner</strong>
                    </td>
                    <td>{caseData.fields.ownerTwoName}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Second Email</strong>
                    </td>
                    <td>{caseData.fields.ownerTwoEmail}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Second Phone</strong>
                    </td>
                    <td>{caseData.fields.ownerTwoPhone}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Email Preferred</strong>
                    </td>
                    <td>
                      {caseData.fields.emailPreferred ? (
                        <CheckSquareIcon className="fill-green-500" />
                      ) : (
                        <SquareIcon className="fill-green-500" />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>SMS Preferred</strong>
                    </td>
                    <td>
                      {caseData.fields.smsPreferred ? (
                        <CheckSquareIcon className="fill-green-500" />
                      ) : (
                        <SquareIcon className="fill-green-500" />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Phone Preferred</strong>
                    </td>
                    <td>
                      {caseData.fields.phonePreferred ? (
                        <CheckSquareIcon className="fill-green-500" />
                      ) : (
                        <SquareIcon className="fill-green-500" />
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <h4 className="mt-2">Case Details</h4>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <strong>Submitted On</strong>
                    </td>
                    <td>
                      {new Date(caseData.fields.intakeDate).toLocaleString(
                        "en-US",
                        {
                          timeZone: "America/Los_Angeles",
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        }
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Submitted By</strong>
                    </td>
                    <td>{caseData.fields.createdBy}</td>
                  </tr>
                  {caseData.fields.submitterInfo && (
                    <tr>
                      <td>
                        <strong>Submitter Info</strong>
                      </td>
                      <td>{caseData.fields.submitterInfo}</td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      <strong>Aquamation Type</strong>
                    </td>
                    <td>{caseData.fields.aquamationType}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Exclude Remembrance Items</strong>
                    </td>
                    <td>
                      {caseData.fields.excludeRemembranceItems ? "Yes" : "No"}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Special Instructions</strong>
                    </td>
                    <td>
                      {caseData.fields.specialInstructions
                        ? caseData.fields.specialInstructions
                        : "None"}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Operational Instructions</strong>
                    </td>
                    <td>
                      {caseData.fields.operationalInstructions
                        ? caseData.fields.operationalInstructions
                        : "None"}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Status</strong>
                    </td>
                    <td>
                      <StatusIcon
                        status={caseData.fields.caseStatus}
                        aquamationType={caseData.fields.aquamationType}
                        returnLogistics={caseData.fields.returnLogistics}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
